export const invalidNameArgumentError = '`name` argument must be a valid string';
export const invalidUnitArgumentError = 'optional `unit` argument must be a valid string';
export const invalidValueArgumentError = 'optional `value` argument must be a valid string';
export const invalidVideoIdArgumentError = 'optional `videoId` argument must be a valid string';
export const missingEventNameError = '`name` property is required for MetroEvent objects';
export const invalidNameError = '`name` property must be a string';
export const invalidVideoIdError = '`videoId` property must be a string';
export const invalidMetricError =
  'optional `metric` property must be an object with `unit` and `value` string properties';
export const missingUnitOrValueError =
  '`unit` and `value` are required in MetroEvent `metric` property';
export const invalidUnitOrValueError = '`unit` and `value` must be strings';

type MetroEventJSON = {
  name: MetroEvent['name'];
  videoId?: MetroEvent['videoId'];
  metric?: MetroEvent['metric'];
};

/**
 * @name MetroEvent
 * @description The `MetroEvent` class provides a helpful utility to create valid
 * `MetroEvent` objects, for use with `MetroHelper`.
 */
export class MetroEvent {
  name: string;

  videoId: string | null = null;

  metric: { unit: string | null; value: string | null } | null = null;

  /**
   * @constructor
   * @param {string} name - the name of the event to send
   * @param {string} unit - the unit of additional value to send with the
   * event (e.g. 'contacts added') (optional)
   * @param {string} value - an additional value to send with the event (e.g. '1', '10') (optional)
   * @param {string} videoId - an additional value to send a video ID with the event (optional)
   */
  constructor(
    name: string,
    unit: string | null = null,
    value: string | null = null,
    videoId: string | null = null,
  ) {
    if (!name || typeof name !== 'string') {
      throw new Error(invalidNameArgumentError);
    }

    if (unit && typeof unit !== 'string') {
      throw new Error(invalidUnitArgumentError);
    }

    if (value && typeof value !== 'string') {
      throw new Error(invalidValueArgumentError);
    }

    if (videoId && typeof videoId !== 'string') {
      throw new Error(invalidVideoIdArgumentError);
    }

    this.name = name;
    this.videoId = videoId;

    if (unit || value) {
      this.metric = {
        unit,
        value,
      };
    }
  }

  /**
   * @name isValid
   * @param obj - Method that validates an existing JS object.
   */
  static isValid(obj: unknown): boolean {
    if (typeof obj !== 'object' || obj === null) {
      throw new Error('Invalid object');
    }

    // quick exit for existing MetroEvents
    if (obj instanceof MetroEvent) {
      return true;
    }

    if (!('name' in obj)) {
      throw new Error(missingEventNameError);
    }

    if (typeof obj.name !== 'string') {
      throw new Error(invalidNameError);
    }

    if (
      'videoId' in obj &&
      obj.videoId &&
      obj.videoId !== null &&
      typeof obj.videoId !== 'string'
    ) {
      throw new Error(invalidVideoIdError);
    }

    if ('metric' in obj) {
      if (typeof obj.metric !== 'object') {
        throw new Error(invalidMetricError);
      }

      if (obj.metric && (!('unit' in obj.metric) || !('value' in obj.metric))) {
        throw new Error(missingUnitOrValueError);
      }

      if (
        obj.metric &&
        (typeof obj.metric.unit !== 'string' || typeof obj.metric.value !== 'string')
      ) {
        throw new Error(invalidUnitOrValueError);
      }
    }

    return true;
  }

  toJSON(): MetroEventJSON {
    const data: MetroEventJSON = { name: this.name };
    if (this.videoId) {
      data.videoId = this.videoId;
    }
    if (this.metric) {
      data.metric = this.metric;
    }
    return data;
  }
}
